import {
  AI_CONSENT,
  type ChatQuestion,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';
import { INTAKE_CHAT_V2_0 } from '@/karehero-app/tell-us-once/intake/content.v2-0';

/**
 * Onboarding 2.0 as it is built on dev: the `talk-first-live` flow on
 * demo/onboarding-2-0-live-facts (onboardingV4Flows.ts, onboardingV4Copy.ts).
 *
 * The same conversation as the design, with what the build changed: once the
 * summary is confirmed it asks the triage questions the voice note left, and
 * what we'd need is one thing per line. The answer to the first question
 * names them, so the build uses their name from there on.
 */
const byID = (id: string): ChatQuestion => {
  const question = INTAKE_CHAT_V2_0.find((q) => q.id === id);
  if (!question) throw new Error(`No 2.0 question ${id}`);
  return question;
};

/** Where to start's one answer. Picked, it ends the chat on booking a call. */
export const BOOK_CALL = 'Book a call with a care expert';

/**
 * The chat opens on the consent, as Keni's first message, rather than on two
 * screens before it: the promise comes first, then the consent, and
 * agreeing is a choice from a group, attached under it. How we use AI is a
 * quieter row that opens the detail in a sheet, in the 2.0 consent's own
 * words. Nothing is sent before it is answered.
 */
const CONSENT: ChatQuestion = {
  id: 'consent',
  attachRows: { more: ['How we use AI'] },
  // What KareHero does comes first, then the one thing that has to come
  // before anything else.
  ask: 'Hi, our care experts help people find, fund and understand care, from working out what you could claim to finding the right carers.\n\nBefore we start, so you know: we use AI to help our care experts read what you share, so you only tell us once. A real person always looks over it.',
  mode: 'rows',
  // Turning it down stays in place, under the yes, but grey.
  asides: [
    { label: "That's fine with me" },
    { label: "I'd rather not", quiet: true },
  ],
  // Without the AI there is no chat to have: the old onboarding instead.
  leaves: "I'd rather not",
  // What KareHero does is the message itself now, so only How we use AI
  // opens a sheet.
  explain: [
    {
      label: 'How we use AI',
      title: 'How we use AI',
      points: [
        'You can tell us about your situation in your own words: typed, spoken, or as a photo of a letter.',
        'We use AI to help our care experts read and organise what you share, so nothing gets lost and you never have to say it twice.',
        'A real person always looks over it.',
      ],
      // The same words as the consent screen's link. No page to go to yet.
      link: { lead: AI_CONSENT.policyLead, label: AI_CONSENT.policyLink },
    },
  ],
  // What the harness fills it with when a later turn is opened.
  answer: "That's fine with me",
};

const HELP: ChatQuestion = {
  ...byID('help'),
  // Keni has already said hello, in the consent.
  // Names the two things we most need: who it's for, and what's happening.
  ask: "Tell me who you're looking after and what's happening with their care right now. Use your own words, and I'll point you to the right help.",

  // The prompts as a checklist, pinned while they answer, ticked off as what
  // they said is read and each one is found in it.
  card: {
    ...byID('help').card!,
    checklistUntil: 'help',
    checklistReads: true,
  },
  reading: {
    lines: [
      "Going through what you've shared",
      'Taking it all in',
      'Working out the right help',
    ],
    ms: 6600,
  },
};

// Not quite is its own turn: what needs changing, then the summary again.
const SUMMARY: ChatQuestion = {
  ...byID('summary'),
  next: { 'Not quite': 'not-quite' },
};

/**
 * The triage questions, only those the summary left unanswered. Margaret's
 * condition and the help she needs at night are in what they said, so
 * where she lives and her age are what is left.
 */
const LOCATION: ChatQuestion = {
  id: 'location',
  ask: `Care and support work a little differently in each part of the UK. We need to know where ${RECIPIENT} lives, so our care experts can give you the right advice.`,
  mode: 'say',
  // More than a pair, so attached under the ask rather than as chips.
  attachRows: {},
  asides: [
    { label: 'England' },
    { label: 'Scotland' },
    { label: 'Wales' },
    { label: 'Northern Ireland' },
    { label: 'Outside the UK' },
  ],
  // Anywhere outside Scotland, Outside the UK included, carries on as England
  // does. Scotland changes what the offer names: see the scotland branch.
  answer: 'England',
};

const AGE: ChatQuestion = {
  id: 'age',
  ask: `How old is ${RECIPIENT}? The help our care experts can offer changes with age.`,
  mode: 'say',
  attachRows: {},
  asides: [
    { label: 'Under 18' },
    { label: '18 to 59' },
    { label: '60 to 65' },
    { label: '66 or over' },
  ],
  answer: '66 or over',
  // Attendance Allowance is for State Pension age, so anyone younger is
  // offered what they could get instead. We don't help under 18s, so they
  // get a kind stop rather than an offer.
  next: {
    'Under 18': 'under-18',
    '18 to 59': 'how-we-help-younger',
    '60 to 65': 'how-we-help-younger',
  },
};

/**
 * Built from what we now know: in England and over pension age. Dev's words
 * (helpLines in onboardingV4Progress). Not right now carries on to what we'd
 * need, as dev does.
 */
const HOW_WE_HELP: ChatQuestion = {
  ...byID('how-we-help'),
  card: {
    label: 'How we can help',
    sentence: true,
    items: [
      `Money ${RECIPIENT} might be entitled to. Attendance Allowance alone could be £76.70 to £114.60 a week, and we fill in the form for you.`,
      `Care at home. We can find carers near ${RECIPIENT} and help you compare them.`,
      'A care expert who knows your situation, to go through it with you.',
    ],
  },
};

/** One thing per line, each ticked on its own as it is found. */
const WHAT_WE_NEED: ChatQuestion = {
  ...byID('what-we-need'),
  // Later skips sending it, straight to where to start, as dev does.
  next: { "I'll do it later": 'start' },
  card: {
    label: "What we'd need",
    sentence: true,
    checklistUntil: 'gather',
    // What the reading takes for each, shown under it as it ticks. Blank
    // where nothing was found.
    found: [
      'Margaret Ellen Doyle',
      '19 March 1943',
      '',
      'Dr Aisha Kerr, Brae Medical Practice',
      '',
      '',
    ],
    items: [
      `${RECIPIENT}'s full name, spelled as it is on their letters`,
      `${RECIPIENT}'s date of birth`,
      `${RECIPIENT}'s National Insurance number`,
      `${RECIPIENT}'s GP`,
      "Any hospital stays they've had in the last year",
      'A phone number, so a care expert can call you',
    ],
  },
};

const GATHER = byID('gather');

/**
 * What everything they sent came to, before what to start with. What we took
 * is in a card from us with a pencil, the same box as the summary, so it can
 * be corrected there or by replying. The values are the intake's own fixtures.
 */
const COLLECTED: ChatQuestion = {
  id: 'collected',
  card: {
    label: "Here's what we've got",
    sentence: true,
    editable: true,
    items: [
      'Full name: Margaret Ellen Doyle',
      'Date of birth: 19 March 1943',
      'GP: Dr Aisha Kerr, Brae Medical Practice',
    ],
  },
  // The same words as the summary's, for the same check.
  ask: 'Does this look right?',
  mode: 'say',
  quickReplies: [{ label: "Yes, that's right" }],
  // What the harness fills it with when a later turn is opened.
  answer: "Yes, that's right",
};

/**
 * Once what we've got is confirmed: what is still missing, as a checklist like
 * What we'd need, pinned while this is open and ticking off if they send any
 * of it now. Do it later moves on without it.
 */
const STILL_NEED: ChatQuestion = {
  id: 'still-need',
  card: {
    // What is left, as what is coming rather than what is missing.
    label: 'Still to come',
    sentence: true,
    checklistUntil: 'still-need',
    items: [
      'Their National Insurance number',
      'Any hospital stays',
      'Your phone number',
    ],
  },
  ask: '',
  mode: 'say',
  multi: true,
  readsEach: true,
  // In their voice, like every chip.
  quickReplies: [{ label: "I'll do this later", go: true }],
  // What the harness fills it with when a later turn is opened.
  answer: "I'll do this later",
};

/**
 * Where the conversation ends for now: what they could get, then into the
 * app, where each of these can be started. A first pass, standing in for What
 * to start with, which stays on the dynamic flow concept until the app can
 * carry on a conversation from Home.
 */
const ELIGIBLE: ChatQuestion = {
  id: 'eligible',
  says: ["Thanks, that's everything for now."],
  card: {
    label: `What ${RECIPIENT} could get`,
    sentence: true,
    items: [
      'Attendance Allowance, £76.70 to £114.60 a week. We fill in the form with you.',
      `Care at home. We can find carers near ${RECIPIENT} and help you compare them.`,
      'A care expert to go through it all with you.',
    ],
  },
  ask: 'You can start on any of this from your home screen. Shall I take you there?',
  mode: 'say',
  quickReplies: [{ label: 'Take me there', go: true }],
  answer: 'Take me there',
  next: { '*': 'end' },
};

/**
 * Once what we've got is confirmed in the chat: where they want to start, as
 * dev has it (START in onboardingV4Copy). Every row ends the chat into the app.
 * Dev checks the details on a screen after this; here that check stays in the
 * chat, as Here's what we've got, before it.
 */
const START: ChatQuestion = {
  id: 'start',
  attachRows: { more: ['Do this later'] },
  ask: 'Great, we have enough to get started. What would you like to do next?',
  mode: 'say',
  answer: BOOK_CALL,
  // The last thing in the chat: whatever they pick, it ends here.
  next: { '*': 'end' },
  // One way on, to a care expert, with Do this later under it.
  asides: [{ label: BOOK_CALL }],
};

/** Said back when a typed phone number will not do. */
export const PHONE_PUSHBACK = {
  short:
    'That number looks too short. Could you check it? A UK number usually has 11 digits.',
  long: 'That number looks too long. Could you check it? A UK number usually has 11 digits.',
  notUk: "That doesn't look like a UK number. It should start with 0 or +44.",
};

/**
 * A UK phone number, mobile or landline, typed however they like: spaces,
 * dashes and +44 are all fine. Ten or eleven digits once +44 is a 0.
 */
const checkPhone = (value: string) => {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('44')) digits = `0${digits.slice(2)}`;
  if (!digits.startsWith('0')) return PHONE_PUSHBACK.notUk;
  if (digits.length < 10) return PHONE_PUSHBACK.short;
  if (digits.length > 11) return PHONE_PUSHBACK.long;
  return undefined;
};

/**
 * A number to reach them on, asked just before where to start. A number that
 * will not do is said back and the question stays open. Do this later is a
 * way on rather than an answer, so it is never checked as a phone number.
 */
const PHONE: ChatQuestion = {
  id: 'phone',
  ask: "What's your phone number? It helps us keep in touch, especially if things change quickly.",
  mode: 'say',
  voice: false,
  placeholder: 'Type your phone number…',
  quickReplies: [{ label: 'Do this later', go: true }],
  check: checkPhone,
  answer: '07700 900461',
  next: { '*': 'start' },
};

/**
 * Shown only when their first answer did not say what they need help with:
 * the one thing the start of the conversation was meant to find out and
 * didn't. An open question, talked or typed, with nothing to pick from. What
 * they say is read like the first answer, and the summary comes back with it
 * in, to check again before where they live. Asked straight after the first
 * summary is confirmed. Off the main path, reached by the need-help branch.
 */
const NEED_HELP: ChatQuestion = {
  id: 'need-help',
  ask: 'So our care experts know where to start, what would you most like help with?',
  mode: 'say',
  reading: {
    lines: ["Going through what you've shared", 'Updating the summary'],
    ms: 3600,
  },
  answer: 'Money Mum might be owed, and care at home.',
  next: { '*': 'summary-need' },
};

/** The summary back with what they need help with in it, to check again. */
const SUMMARY_NEED: ChatQuestion = {
  ...byID('summary'),
  id: 'summary-need',
  says: ["Thanks. Here's the updated summary."],
  next: { 'Not quite': 'not-quite', '*': 'location' },
};

/*
 * Off the main path. Each is reached by an answer's `next`, and each either
 * rejoins the main path or ends in the app.
 */

/** Not quite on the summary: what needs changing, said or typed. */
const NOT_QUITE: ChatQuestion = {
  id: 'not-quite',
  ask: "What have we got wrong? Talk or type, and we'll put it right.",
  mode: 'say',
  placeholder: 'Or type it…',
  reading: {
    lines: ["Going through what you've shared", 'Updating the summary'],
    ms: 3600,
  },
  answer: "It's more like 50 hours a week now",
  next: { '*': 'summary-fixed' },
};

/** The summary back with the change in it, to check again. */
const SUMMARY_FIXED: ChatQuestion = {
  ...byID('summary'),
  id: 'summary-fixed',
  says: ["Thanks for letting us know. Here's the updated summary."],
  card: {
    ...byID('summary').card!,
    items: [
      byID('summary').card!.items![0].replace('40 hours', '50 hours'),
      ...byID('summary').card!.items!.slice(1),
    ],
  },
  next: { 'Not quite': 'not-quite', '*': 'location' },
};

/** Under State Pension age: Personal Independence Payment, in dev's words. */
const HOW_WE_HELP_YOUNGER: ChatQuestion = {
  ...HOW_WE_HELP,
  id: 'how-we-help-younger',
  card: {
    ...HOW_WE_HELP.card!,
    items: [
      `Money ${RECIPIENT} might be entitled to, like Personal Independence Payment, and we'll help you apply.`,
      ...HOW_WE_HELP.card!.items!.slice(1),
    ],
  },
  next: { '*': 'what-we-need' },
};

/**
 * Under 18: we only help with care for adults, so the chat says so plainly and
 * points somewhere that can help, rather than offering what we can't do.
 * Changing the age goes back to the question, in case it was a slip. Nothing
 * else moves it on: there is no app for them to go into, so the chat stops on
 * this message rather than ending into Home.
 */
const UNDER_18: ChatQuestion = {
  id: 'under-18',
  ask: "We're sorry, we can only help with care for adults at the moment. For a child or young person, your local council's children's services is a good place to start.",
  mode: 'rows',
  attachRows: {},
  asides: [{ label: 'Change the age' }],
  next: { '*': 'age' },
};

/**
 * What the live captions hear while the first question is answered by voice.
 * The same situation the summary reads back, said the way somebody says it.
 */
export const LIVE_CAPTION_SCRIPT =
  "So I look after my mum, she's eighty four and she has dementia, and it's got a lot worse this year. I'm doing about forty hours a week now, and my sister helps out at weekends. She's started getting up in the night and I'm worried about her being on her own. I don't really know what money she could get, or where to start with any of it.";

/**
 * The same, without what they need help with: the branch where the reading
 * finds two of the three things the prompts card asks about.
 */
export const LIVE_CAPTION_SCRIPT_MISSING =
  "So I look after my mum, she's eighty four and she has dementia, and it's got a lot worse this year. I'm doing about forty hours a week now, and my sister helps out at weekends. She's started getting up in the night and I'm worried about her being on her own.";

/**
 * The whole journey, the parked middle included: how we can help, what we'd
 * need, sending it and what we've got. Kept for later, and for the scenes and
 * branches that show those turns.
 */
export const INTAKE_CHAT_V2_0_DEV_FULL: ChatQuestion[] = [
  CONSENT,
  HELP,
  SUMMARY,
  LOCATION,
  AGE,
  HOW_WE_HELP,
  WHAT_WE_NEED,
  GATHER,
  COLLECTED,
  PHONE,
  START,
  // Off the main path, after its end.
  NOT_QUITE,
  SUMMARY_FIXED,
  HOW_WE_HELP_YOUNGER,
  UNDER_18,
  NEED_HELP,
  SUMMARY_NEED,
];

/**
 * The journey as it stands: once we know where they live and how old they
 * are, their phone number, then where to start. The middle is parked, not deleted; it
 * stays in the list, stepped over, so it can come back by changing one `next`.
 */
export const INTAKE_CHAT_V2_0_DEV: ChatQuestion[] =
  INTAKE_CHAT_V2_0_DEV_FULL.map((q) =>
    q.id === 'age'
      ? { ...q, next: { 'Under 18': 'under-18', '*': 'phone' } }
      : q,
  );

/**
 * Off the happy path: the summary mishears forty hours as fourteen, and they
 * correct it by typing. The reading reads the correction and the summary
 * comes back with it fixed, to be confirmed again.
 */
const SUMMARY_CARD = SUMMARY.card!;
const SUMMARY_MISHEARD: ChatQuestion = {
  ...SUMMARY,
  card: {
    ...SUMMARY_CARD,
    items: [
      SUMMARY_CARD.items![0].replace('40 hours', '14 hours'),
      ...SUMMARY_CARD.items!.slice(1),
    ],
  },
  // What the harness fills it with when a later turn is opened.
  answer: "It's forty, not fourteen",
};

const SUMMARY_CORRECTED: ChatQuestion = {
  ...SUMMARY,
  id: 'summary-corrected',
  says: ["Thanks for letting us know. Here's the updated summary."],
};

/**
 * Where it goes once the chat is dynamic, kept off the onboarding dev build:
 * once what we've got is confirmed, what is still to come and what they could
 * get, in place of where to start. Opened by the dynamic flow concepting page.
 */
export const INTAKE_CHAT_V2_0_DYNAMIC: ChatQuestion[] =
  INTAKE_CHAT_V2_0_DEV_FULL.flatMap((q) =>
    q.id === 'start' ? [STILL_NEED, ELIGIBLE] : [q],
  );

export const INTAKE_CHAT_V2_0_DEV_CORRECTED: ChatQuestion[] =
  INTAKE_CHAT_V2_0_DEV.flatMap((q) =>
    q.id === 'summary' ? [SUMMARY_MISHEARD, SUMMARY_CORRECTED] : [q],
  );

/** The dev list with one question's harness answer changed, to open a branch. */
const answering = (
  id: string,
  answer: string,
  list: ChatQuestion[] = INTAKE_CHAT_V2_0_DEV,
): ChatQuestion[] => list.map((q) => (q.id === id ? { ...q, answer } : q));

/**
 * Caring for themselves rather than someone else: the same conversation, about
 * "you" rather than about Margaret.
 */
export const LIVE_CAPTION_SCRIPT_SELF =
  "It's just me really. I had a stroke last year and I live on my own. I manage most days, but washing and getting dressed take me ages, and I've had a couple of falls. My daughter pops in when she can. I don't know if there's any money I could get.";

const SELF: Record<string, Partial<ChatQuestion>> = {
  summary: {
    card: {
      ...SUMMARY.card!,
      items: [
        'You live on your own, and your daughter pops in when she can.',
        "Since your stroke last year, washing and dressing take a long time, and you've had a couple of falls.",
        "You'd like to know what money you could get.",
      ],
    },
  },
  location: {
    ask: 'Care and support work a little differently in each part of the UK. We need to know where you live, so our care experts can give you the right advice.',
  },
  age: {
    ask: 'How old are you? The help our care experts can offer changes with age.',
  },
  'under-18': {
    ask: "We're sorry, we can only help with care for adults at the moment. Your GP or your local council can point you to the right support.",
  },
  'how-we-help': {
    card: {
      ...HOW_WE_HELP.card!,
      items: [
        'Money you might be entitled to. Attendance Allowance alone could be £76.70 to £114.60 a week, and we fill in the form for you.',
        'Care at home. We can find carers near you and help you compare them.',
        'A care expert who knows your situation, to go through it with you.',
      ],
    },
  },
  'what-we-need': {
    card: {
      ...WHAT_WE_NEED.card!,
      found: ['', '', '', '', '', ''],
      items: [
        'Your full name, spelled as it is on your letters',
        'Your date of birth',
        'Your National Insurance number',
        'Your GP',
        "Any hospital stays you've had in the last year",
        'A phone number, so a care expert can call you',
      ],
    },
  },
};

/**
 * Said in their own words in place of tapping where she lives, with her age
 * in it too. Dev takes it as the answer, says thanks, and skips the age
 * question because it is already answered.
 */
export const OWN_WORDS_LOCATION = "She's 84 and lives in Leeds";

/** Opened by Flow's `branch`. Each opens the path its name says. */
export const INTAKE_CHAT_V2_0_DEV_BRANCHES = {
  'not-quite': answering('summary', 'Not quite'),
  // The rest show the parked middle, so they walk the whole journey.
  full: INTAKE_CHAT_V2_0_DEV_FULL,
  'self-full': INTAKE_CHAT_V2_0_DEV_FULL.map((q) => ({ ...q, ...SELF[q.id] })),
  // In Scotland: Pension Age Disability Payment in place of Attendance
  // Allowance, at the same rates, as in dev.
  scotland: answering('location', 'Scotland', INTAKE_CHAT_V2_0_DEV_FULL).map(
    (q) =>
      q.id === 'how-we-help'
        ? {
            ...q,
            card: {
              ...q.card!,
              items: [
                `Money ${RECIPIENT} might be entitled to. Pension Age Disability Payment alone could be £76.70 to £114.60 a week, and we fill in the form for you.`,
                ...q.card!.items!.slice(1),
              ],
            },
          }
        : q,
  ),
  younger: answering('age', '18 to 59', INTAKE_CHAT_V2_0_DEV_FULL),
  // Under 18: the kind stop in place of the rest.
  'under-18': answering('age', 'Under 18'),
  // The phone number left for later, on to where to start.
  'phone-later': answering('phone', 'Do this later'),
  // Their first answer left out what they need help with: once the summary is
  // confirmed, the one question that asks it, before where they live.
  // Its summary has no line about what they need help with, since they did
  // not say; the updated one after has it.
  'need-help': INTAKE_CHAT_V2_0_DEV.map((q) =>
    q.id === 'summary'
      ? {
          ...q,
          card: { ...q.card!, items: q.card!.items!.slice(0, 2) },
          next: { 'Not quite': 'not-quite', '*': 'need-help' },
        }
      : q,
  ),
  // No help needed day to day: the care at home line drops out, as in dev.
  'no-daily-help': INTAKE_CHAT_V2_0_DEV_FULL.map((q) =>
    q.id === 'how-we-help'
      ? {
          ...q,
          card: {
            ...q.card!,
            items: q.card!.items!.filter((line) => !line.startsWith('Care')),
          },
        }
      : q,
  ),
  'not-now': answering(
    'how-we-help',
    'Not right now',
    INTAKE_CHAT_V2_0_DEV_FULL,
  ),
  'own-words': INTAKE_CHAT_V2_0_DEV_FULL.map((q) =>
    q.id === 'location'
      ? { ...q, answer: OWN_WORDS_LOCATION, next: { '*': 'how-we-help' } }
      : q.id === 'how-we-help'
        ? { ...q, says: ["Thanks. Here's how we can help."] }
        : q,
  ),
  later: answering(
    'what-we-need',
    "I'll do it later",
    INTAKE_CHAT_V2_0_DEV_FULL,
  ),
  self: INTAKE_CHAT_V2_0_DEV.map((q) => ({ ...q, ...SELF[q.id] })),
};

export type IntakeDevBranch = keyof typeof INTAKE_CHAT_V2_0_DEV_BRANCHES;

/** The first answer, typed. The same situation the voice note gives. */
export const HELP_TYPED =
  "I look after my mum, she's 84 and has dementia. It's got worse this year and I'm worried about money.";

/** What Keni says when the first answer does not get through. */
export const HELP_STATES = {
  micBlocked:
    "I can't hear you yet. To talk to me, let KareHero use your microphone in your phone's settings. Or you can type instead.",
  notUnderstood:
    "Sorry, I couldn't make that out. Could you try again, or type it if that's easier?",
  tooShort:
    "Thanks. Could you tell me a bit more? Who you look after, what that involves, and what you'd like help with.",
  resumed:
    'Welcome back. We were just getting started on this. Shall we carry on?',
};
